import { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import DataTableLayer from "../../components/DataTableLayer";
import PostByAgg, { downloadFileFromApi } from "../../utils/axios";
import { useAuth } from "../../context/AuthContext";
import ModalAlert from "../../components/ModalAlert";
import LottieLoading from "../../components/child/LottieLoading";

const DEFAULT_PAGE_SIZE = 10;

// kamu bisa atur ini: "ALL" akan mengambil data dari tahun ini mundur sekian tahun
const MAX_YEARS_BACK = 10;

// aman untuk API yang punya limit page_size
const CHUNK_SIZE = 500;

const Reporting = () => {
  const [listReport, setListReport] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    totalRecords: 0,
    totalPages: 0,
  });

  // ✅ default ALL
  const [filters, setFilters] = useState({
    year: "ALL",
    channel: "ALL",
  });

  const [modalOpen, setModalOpen] = useState(<div></div>);
  const { musicLabelCode } = useAuth();

  // ====== Columns ======
  const Columns = [
    { header: "Label name", accessorKey: "music_label_name" },
    { header: "Channel", accessorKey: "channel_name" },
    { header: "Date", accessorKey: "time" },
    {
      header: "Total Stream",
      accessorKey: "stream_view",
      cell: (info) => {
        const raw = info.getValue();
        const n =
          typeof raw === "number"
            ? raw
            : parseFloat(String(raw ?? "0").replace(/[^\d.-]/g, "")) || 0;

        return (
          <span style={{ whiteSpace: "nowrap" }}>
            {n.toLocaleString("id-ID", { maximumFractionDigits: 0 })}
          </span>
        );
      },
    },
    {
      header: "Revenue",
      accessorKey: "amount",
      cell: (info) => {
        const raw = info.getValue();
        const num =
          typeof raw === "number"
            ? raw
            : parseFloat(String(raw ?? "0").replace(/[^\d.-]/g, "")) || 0;

        return (
          <span style={{ whiteSpace: "nowrap" }}>
            Rp {num.toLocaleString("id-ID")}
          </span>
        );
      },
    },
    {
      accessorKey: "action",
      header: "Actions",
      cell: ({ row }) => (
        <div className="dropdown">
          <button
            className="btn px-18 py-11 text-primary-light"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <Icon icon="ph:dots-three-outline-fill" className="menu-icon" />
          </button>
          <ul className="dropdown-menu">
            <li>
              <Link
                className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleDownload();
                }}
              >
                Download
              </Link>
            </li>
          </ul>
        </div>
      ),
    },
  ];

  // ====== Year options (ALL + otomatis bertambah tiap tahun) ======
  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const arr = [];
    for (let i = 0; i <= MAX_YEARS_BACK; i++) arr.push(String(now - i));
    return arr;
  }, []);

  const filterConfigs = [
    { key: "year", label: "Year", options: yearOptions },
    { key: "channel", label: "Channel", column: "channel_name" },
  ];

  const handleFilterChange = (f) => {
    setFilters(f);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // ====== Helper: promisify PostByAgg ======
  const postPaymentListByMonth = ({ year, page, pageSize }) =>
    new Promise((resolve, reject) => {
      PostByAgg({
        url: "song/v1/payment/list-by-month",
        body: {
          keyword: "",
          page,
          page_size: pageSize,
          music_label_code: musicLabelCode,
          music_label_name: "",
          year, // ⚠️ API kamu wajib year, jadi ALL tidak boleh kirim "ALL"
        },
        onResponse: resolve,
        onError: reject,
      });
    });

  // ====== Fetch 1 tahun (ambil semua data, chunked) ======
  const fetchAllRowsForYear = async (year) => {
    // 1) ambil total_records (pakai page_size kecil)
    const metaRes = await postPaymentListByMonth({
      year,
      page: 1,
      pageSize: 1,
    });
    const total = metaRes?.data?.data?.total_records ?? 0;

    if (!total) return [];

    // 2) ambil data per chunk, lalu gabungkan
    const pages = Math.ceil(total / CHUNK_SIZE);
    const allRows = [];

    for (let p = 1; p <= pages; p++) {
      const res = await postPaymentListByMonth({
        year,
        page: p,
        pageSize: CHUNK_SIZE,
      });

      const rows = res?.data?.data?.data ?? [];
      if (Array.isArray(rows) && rows.length) allRows.push(...rows);
    }

    return allRows;
  };

  // ====== Fetch ALL years (gabung beberapa tahun) ======
  const fetchAllYears = async () => {
    const now = new Date().getFullYear();
    const yearsToTry = [];
    for (let i = 0; i <= MAX_YEARS_BACK; i++) yearsToTry.push(String(now - i));

    const combined = [];

    // sequential (lebih aman untuk API rate-limit)
    for (const y of yearsToTry) {
      const rows = await fetchAllRowsForYear(y);
      if (rows.length) {
        // optional: tandai tahunnya untuk debugging/opsi display
        combined.push(...rows.map((r) => ({ ...r, _year: y })));
      }
    }

    // optional: sort by time desc (kalau field "time" bisa diparse)
    combined.sort((a, b) => {
      const da = new Date(a.time);
      const db = new Date(b.time);
      const ta = isNaN(da.getTime()) ? 0 : da.getTime();
      const tb = isNaN(db.getTime()) ? 0 : db.getTime();
      return tb - ta;
    });

    return combined;
  };

  // ====== Load data utama ======
  const loadData = async () => {
    if (!musicLabelCode) return;

    setIsLoading(true);
    try {
      let rows = [];

      if (filters.year === "ALL") {
        // ✅ karena API ALL/null hasilnya kosong, kita gabung per tahun
        rows = await fetchAllYears();
      } else {
        rows = await fetchAllRowsForYear(String(filters.year));
      }

      // (opsional) filter channel di client kalau mau dropdown channel beneran bekerja
      if (filters.channel && filters.channel !== "ALL") {
        rows = rows.filter(
          (r) => String(r.channel_name) === String(filters.channel),
        );
      }

      setListReport(rows);

      // update pagination info untuk DataTableLayer (client-side)
      const totalRecords = rows.length;
      const totalPages = Math.ceil(totalRecords / pagination.pageSize) || 0;

      setPagination((prev) => ({
        ...prev,
        totalRecords,
        totalPages,
      }));
    } catch (error) {
      setModalOpen(
        <ModalAlert
          show={true}
          type={"failed"}
          text={
            error?.response?.data || "Terjadi kesalahan saat mengambil data."
          }
          action={() => {}}
        />,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ====== Download (ALL = gabung years dulu, atau export per year) ======
  const handleDownload = async () => {
    try {
      setIsLoading(true);

      // Kalau API export juga tidak support ALL (kemungkinan besar), maka:
      // - untuk ALL: lebih aman export per-year (atau bikin file gabungan di frontend)
      // Di sini aku buat sederhana: kalau ALL, export tahun sekarang saja.
      // Kalau kamu mau export gabungan ALL, bilang ya, nanti aku buatin gabung XLSX di frontend.

      if (filters.year === "ALL") {
        setModalOpen(
          <ModalAlert
            show={true}
            type={"failed"}
            text={
              "API export tidak mendukung ALL year. Export per tahun dulu ya (pilih tahun)."
            }
            action={() => {}}
          />,
        );
        return;
      }

      const year = String(filters.year);

      await downloadFileFromApi({
        url: "song/v1/payment/list-by-month-export",
        body: {
          keyword: "",
          page: 1,
          page_size:
            pagination.totalRecords > 0 ? pagination.totalRecords : CHUNK_SIZE,
          music_label_code: musicLabelCode,
          music_label_name: "",
          year,
        },
        fileName: `payment-export-${year}.xlsx`,
        onResponse: () => setIsLoading(false),
        onError: (error) => {
          setIsLoading(false);
          setModalOpen(
            <ModalAlert
              show={true}
              type={"failed"}
              text={error?.response?.data || "Gagal mengunduh file."}
              action={() => {}}
            />,
          );
        },
      });
    } catch {
      setIsLoading(false);
      setModalOpen(
        <ModalAlert
          show={true}
          type={"failed"}
          text={"Gagal mengunduh file."}
          action={() => {}}
        />,
      );
    }
  };

  // ✅ Load ulang ketika filter berubah / musicLabelCode ready
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicLabelCode, filters.year, filters.channel]);

  return (
    <div>
      <DataTableLayer
        data={listReport || []}
        columns={Columns}
        title={"Reporting"}
        pagination={pagination}
        filterConfigs={filterConfigs}
        onFilterChange={handleFilterChange}
        showButton={false}
      />

      {isLoading && <LottieLoading text="Loading..." />}
      {modalOpen}
    </div>
  );
};

export default Reporting;
